import { graphqlQuery } from '@/lib/api/graphql';
import { fetchAllConnectionNodes, type GraphQLConnection } from '@/lib/graphql-pagination';
import { formatWeekRangeLabel, startOfWeekMonday, toLocalDateString } from '@/lib/utils';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- Types ---

export interface WeeklyCollectionTrendPoint {
  week: string;
  weekStartDate: string;
  collected: number;
  transactionCount: number;
}

export interface WeeklyCollectionTrendResult {
  points: WeeklyCollectionTrendPoint[];
  totalCollected: number;
  averagePerWeek: number;
}

// --- GraphQL ---

interface WeeklyCollectionTrendNode {
  creditControllerId: number | null;
  weekStartDate: string;
  collectedAmount: number;
  transactionCount: number;
}

interface WeeklyCollectionTrendListResponse {
  dashboard: {
    weeklyCollectionTrendList: GraphQLConnection<WeeklyCollectionTrendNode>;
  };
}

const WEEKLY_COLLECTION_TREND_QUERY = `
  query WeeklyCollectionTrendList($first: Int, $after: String, $sort: [WeeklyCollectionTrendSortSortByInput!], $filter: WeeklyCollectionTrendFilterInput) {
    dashboard {
      weeklyCollectionTrendList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            creditControllerId
            weekStartDate
            collectedAmount
            transactionCount
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// Paging is offset-based, so the sort must be a total order over the view's grain
// (organisation and company are fixed per request) or rows can repeat or be skipped
// between pages.
export const WEEKLY_COLLECTION_TREND_SORT = [
  { field: 'WeekStartDate', order: 'Ascending' },
  { field: 'CreditControllerId', order: 'Ascending' },
  { field: 'CurrencyCode', order: 'Ascending' },
];

// --- Mock data generator ---

function generateMockTrend(from: Date, to: Date): WeeklyCollectionTrendPoint[] {
  const points: WeeklyCollectionTrendPoint[] = [];
  const cursor = startOfWeekMonday(from);
  let seed = from.getTime();
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) & 0xffffffff;
    return Math.abs(seed) / 0xffffffff;
  };
  while (cursor <= to) {
    points.push({
      week: formatWeekRangeLabel(cursor),
      weekStartDate: toLocalDateString(cursor),
      collected: Math.round(100000 + rand() * 80000),
      transactionCount: Math.round(5 + rand() * 20),
    });
    cursor.setDate(cursor.getDate() + 7);
  }
  return points;
}

// --- Hook ---

export function useWeeklyCollectionTrendGraphQL(
  selectedUserId?: string,
  dateFrom?: Date,
  dateTo?: Date,
  currencyCode?: string,
) {
  const apiConfig = useApiConfig();

  const effectiveFrom = dateFrom ?? new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  const effectiveTo = dateTo ?? new Date();

  const mockPoints = generateMockTrend(effectiveFrom, effectiveTo);
  const mockTotal = mockPoints.reduce((s, p) => s + p.collected, 0);
  const mockResult: WeeklyCollectionTrendResult = {
    points: mockPoints,
    totalCollected: mockTotal,
    averagePerWeek: mockPoints.length > 0 ? Math.round(mockTotal / mockPoints.length) : 0,
  };

  return useModeAwareQuery<WeeklyCollectionTrendResult, Error>(
    ['weekly-collection-trend', selectedUserId, dateFrom?.toISOString(), dateTo?.toISOString(), currencyCode],
    async ({ signal }) => {
      const filter: Record<string, unknown> = {};

      if (selectedUserId && selectedUserId !== 'everyone') {
        filter.creditControllerId = { eq: Number(selectedUserId) };
      }

      if (currencyCode) {
        filter.currencyCode = { eq: currencyCode };
      }

      // weekStartDate is the Monday of the week, so bound by the Mondays of the
      // weeks containing the selected dates to include both boundary weeks.
      filter.weekStartDate = {
        ge: toLocalDateString(startOfWeekMonday(effectiveFrom)),
        le: toLocalDateString(startOfWeekMonday(effectiveTo)),
      };

      // Rows are per credit controller and ordered by weekStartDate ascending,
      // so a single truncated page would silently drop the most recent weeks.
      const nodes = await fetchAllConnectionNodes<WeeklyCollectionTrendNode>(async (after) => {
        const response = await graphqlQuery<WeeklyCollectionTrendListResponse>(
          apiConfig,
          WEEKLY_COLLECTION_TREND_QUERY,
          { first: 1000, after, sort: WEEKLY_COLLECTION_TREND_SORT, filter },
          signal,
        );
        return response.dashboard.weeklyCollectionTrendList;
      });

      // Sort by weekStartDate ascending
      nodes.sort((a, b) => a.weekStartDate.localeCompare(b.weekStartDate));

      const points: WeeklyCollectionTrendPoint[] = nodes.map((x) => ({
        week: formatWeekRangeLabel(x.weekStartDate),
        weekStartDate: x.weekStartDate,
        collected: x.collectedAmount,
        transactionCount: x.transactionCount,
      }));

      const totalCollected = points.reduce((s, p) => s + p.collected, 0);

      return {
        points,
        totalCollected,
        averagePerWeek: points.length > 0 ? Math.round(totalCollected / points.length) : 0,
      };
    },
    mockResult,
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );
}

// --- Hook for all users (team overview) ---

export function useWeeklyCollectionTrendAllUsers(dateFrom?: Date, dateTo?: Date, currencyCode?: string) {
  const apiConfig = useApiConfig();

  const effectiveFrom = dateFrom ?? new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  const effectiveTo = dateTo ?? new Date();

  return useModeAwareQuery<Map<number, number>, Error>(
    ['weekly-collection-trend-all-users', dateFrom?.toISOString(), dateTo?.toISOString(), currencyCode],
    async ({ signal }) => {
      const filter: Record<string, unknown> = {
        weekStartDate: {
          ge: toLocalDateString(startOfWeekMonday(effectiveFrom)),
          le: toLocalDateString(startOfWeekMonday(effectiveTo)),
        },
      };

      if (currencyCode) {
        filter.currencyCode = { eq: currencyCode };
      }

      const nodes = await fetchAllConnectionNodes<WeeklyCollectionTrendNode>(async (after) => {
        const response = await graphqlQuery<WeeklyCollectionTrendListResponse>(
          apiConfig,
          WEEKLY_COLLECTION_TREND_QUERY,
          { first: 1000, after, sort: WEEKLY_COLLECTION_TREND_SORT, filter },
          signal,
        );
        return response.dashboard.weeklyCollectionTrendList;
      });

      // Group by creditControllerId and sum collectedAmount
      const byUser = new Map<number, number>();
      for (const node of nodes) {
        if (node.creditControllerId !== null) {
          const current = byUser.get(node.creditControllerId) ?? 0;
          byUser.set(node.creditControllerId, current + node.collectedAmount);
        }
      }

      return byUser;
    },
    new Map<number, number>(),
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );
}
